import { TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { filter, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LocalizationService } from '../../core/services/localization.service';
import { isTenantAdmin } from '../../models/enums/user-role.enum';
import { AuthService } from '../../services/auth.service';
import { BillingStatusService } from '../../services/billing-status.service';
import { OnboardingDataService } from '../../services/onboarding-data.service';
import { WineryContextService } from '../../services/winery-context.service';
import { BottomTabBarComponent } from '../components/bottom-tab-bar/bottom-tab-bar.component';
import { FormLoadErrorComponent } from '../components/form/form-load-error.component';
import { GettingStartedReturnComponent } from '../components/getting-started-return/getting-started-return.component';
import { SizeMessageComponent } from '../components/size-message/size-message.component';
import { TopBarComponent } from '../components/top-bar/top-bar.component';
import { CardBodyDirective } from '../directives/card-body.directive';
import { CardDirective } from '../directives/card.directive';
import { EnvironmentLevelPipe } from '../pipes/environment-level.pipe';
import { ScreenSize, ScreenSizeService } from '../../services/screen-size.service';
import { formatShortDateWithYear, localDate } from '../utils/dates';

const PLAN_PATH = '/settings/plan';
const PAGES_WITH_OWN_CLOSED_LINE = [PLAN_PATH, '/settings/website'];

/** The sizes of the deepest route that declares `data.sizes`; null when none does. */
function activeSizes(root: ActivatedRouteSnapshot): readonly ScreenSize[] | null {
  let sizes: readonly ScreenSize[] | null = null;
  for (let route: ActivatedRouteSnapshot | null = root; route; route = route.firstChild) {
    sizes = (route.data['sizes'] as readonly ScreenSize[] | undefined) ?? sizes;
  }
  return sizes;
}

// Chrome per size (design.md § 7): computer top bar + footer, tablet slim top bar + tab bar, phone none. The top bar and
// the tab bar are mutually exclusive in the DOM (not just hidden) so the page has at most one navigation landmark.

@Component({
  standalone: true,
  imports: [RouterModule, TopBarComponent, BottomTabBarComponent, SizeMessageComponent, TranslocoDirective, TitleCasePipe, EnvironmentLevelPipe, CardDirective, CardBodyDirective, GettingStartedReturnComponent, FormLoadErrorComponent],
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellComponent {
  private readonly _router = inject(Router);
  private readonly _wineryContext = inject(WineryContextService);
  private readonly _billing = inject(BillingStatusService);
  private readonly _onboarding = inject(OnboardingDataService);
  private readonly _localization = inject(LocalizationService);
  private readonly _auth = inject(AuthService);
  private readonly _profile = toSignal(this._auth.currentProfile$);

  public readonly profileFailed = this._auth.profileFailed;

  public readonly size = inject(ScreenSizeService).size;
  private readonly _isAdmin = computed(() => isTenantAdmin(this._profile()?.role));

  // The outlet is only removed, not the route: when the window widens it re-attaches the active route without a reload.
  private readonly _routeSizes = toSignal(
    this._router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => activeSizes(this._router.routerState.snapshot.root)),
    ),
    { initialValue: activeSizes(this._router.routerState.snapshot.root) },
  );
  public readonly supported = computed(() => this._routeSizes()?.includes(this.size()) ?? true);

  // The plan page and Website and links each say the page is closed themselves, so the notice stays off them.
  private readonly _url = toSignal(
    this._router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this._router.url),
    ),
    { initialValue: this._router.url },
  );
  public readonly closedNotice = computed(() => this._billing.closed() && !PAGES_WITH_OWN_CLOSED_LINE.includes(this._url().split(/[?#]/)[0] ?? ''));

  // A step page opened from Getting started carries `from=start`; the box stays while a step is open.
  public readonly showStartReturn = computed(() => this._router.parseUrl(this._url()).queryParams['from'] === 'start' && this._onboarding.openCount() > 0);

  public readonly closedReason = this._billing.closedReason;
  public readonly trialEndedOn = computed(() => {
    const endsAt = this._billing.trialEndsAt();
    const timeZone = this._wineryContext.timezone();
    return endsAt ? formatShortDateWithYear(localDate(endsAt, timeZone), this._localization.activeLocale()) : '';
  });

  public readonly winery = this._wineryContext.winery;
  public readonly wineryLoading = this._wineryContext.loading;
  public readonly version = environment.buildVersion;
  public readonly environmentName = environment.envLevel;

  constructor() {
    this._wineryContext.load();
    effect(() => {
      const admin = this._isAdmin();
      untracked(() => {
        // Reset first: the previous sign-in's answers must not count as settled for the new user.
        this._billing.reset();
        this._onboarding.reset();
        if (admin) {
          this._billing.load();
          this._onboarding.load().subscribe();
        }
      });
    });
    effect(() => {
      if (!this._isAdmin() || this.size() !== 'computer' || !this._billing.closed() || !this._billing.configured()) return;
      const tenantId = this._profile()?.tenantId;
      if (tenantId) untracked(() => this._showPlanOnce(tenantId));
    });
  }

  protected retryProfile(): void {
    // A failure sets profileFailed again; the banner stays and needs no second handler.
    this._auth.getProfile().subscribe({ error: () => undefined });
  }

  /** The owner meets the plan choice once per browser; any storage failure skips it so a blocked store can never trap the owner in a redirect. */
  private _showPlanOnce(tenantId: string): void {
    const key = `tinello.planShown.${tenantId}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      return;
    }
    if (this._router.url.split(/[?#]/)[0] !== PLAN_PATH) void this._router.navigateByUrl(PLAN_PATH);
  }
}
